import * as THREE from 'three';

// ── LIGHT THAT LIVES IN A TEXTURE ────────────────────────────────────────────
//
// The street lamps light the street through ONE texture laid flat over the
// city: every lamp's pool of light, painted once from above. Each city surface
// looks up the texel under it and adds that light — a single texture read, no
// matter how many lamps there are.
//
// The alternative, real point lights, is the thing this world already learned
// the hard way: every lit pixel loops over every point light, the count is
// compiled into every shader, and twenty-six resident lamps cost 37 ms a frame
// (OVERNIGHT_2026-09-23). A hundred street lamps that way would be unplayable.
// A light map costs the same at one lamp or a thousand, and on a phone.
//
// What it gives up: pools are flat projections, so a lamp lights a wall facing
// it and the wall facing away the same. Walls are attenuated with height and
// leaned toward up-facing surfaces, which reads right at street level.
//
// Also here: WET ASPHALT. After dark the road's roughness drops in noise-shaped
// patches, so the reflection environment (the sky, the city's neon strips)
// slides across the street as you move — the rained-on look of a night city.
//
// One set of uniforms, shared by reference with every patched material, so a
// single write per frame (StreetLights) drives the whole city.

export const streetU = {
  uPoolTex: { value: null },
  uPoolRect: { value: new THREE.Vector4(0, 0, 1, 1) },   // minX, minZ, sizeX, sizeZ
  uPoolI: { value: 0 },
  uWet: { value: 0 },      // night sheen on asphalt
  uWetAll: { value: 0 },   // rain: every flat surface, and walls a little
};

// Until the city has been read, the pool texture is one black texel — the
// uniform must point at a real texture from the first compile.
const BLACK = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1);
BLACK.needsUpdate = true;
streetU.uPoolTex.value = BLACK;

const VERT_DECL = /* glsl */`
varying vec3 vStreetWorld;
`;
const VERT_BODY = /* glsl */`
{
  vec4 sw = vec4( transformed, 1.0 );
  #ifdef USE_BATCHING
    sw = batchingMatrix * sw;
  #endif
  #ifdef USE_INSTANCING
    sw = instanceMatrix * sw;
  #endif
  vStreetWorld = ( modelMatrix * sw ).xyz;
}
`;

const FRAG_DECL = /* glsl */`
varying vec3 vStreetWorld;
uniform sampler2D uPoolTex;
uniform vec4 uPoolRect;
uniform float uPoolI;
uniform float uWet;
uniform float uWetAll;
float streetHash(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
float streetNoise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(streetHash(i), streetHash(i + vec2(1.0, 0.0)), u.x), mix(streetHash(i + vec2(0.0, 1.0)), streetHash(i + vec2(1.0, 1.0)), u.x), u.y);
}
`;

// Before lighting: wet roads get glossy. Only dark, near-flat, near-ground
// texels — asphalt — so pavements, kerbs, roofs and walls stay dry.
const FRAG_ROUGH = /* glsl */`
#ifdef STREET_WET
{
  // The shaded normal is not computed until after this point, so the
  // geometric one: interpolated, or from screen derivatives when flat-shaded.
  #ifdef FLAT_SHADED
    vec3 swnV = normalize( cross( dFdx( vViewPosition ), dFdy( vViewPosition ) ) );
  #else
    vec3 swnV = normalize( vNormal );
  #endif
  vec3 swn = inverseTransformDirection( swnV, viewMatrix );
  float up = smoothstep( 0.85, 0.97, abs( swn.y ) );
  float ground = up * ( 1.0 - smoothstep( 0.3, 0.8, vStreetWorld.y ) );
  // DRY GROUND IS MATTE. The model gives its asphalt a glossy 0.37, which by
  // day mirrors the blue sky across every road. Real dry asphalt and paving
  // sit near 0.8; wetness (below) takes it back down where it should be shiny.
  roughnessFactor = mix( roughnessFactor, max( roughnessFactor, 0.8 ), ground );
}
if ( uWet > 0.001 || uWetAll > 0.001 ) {
  #ifdef FLAT_SHADED
    vec3 swnV = normalize( cross( dFdx( vViewPosition ), dFdy( vViewPosition ) ) );
  #else
    vec3 swnV = normalize( vNormal );
  #endif
  vec3 swn = inverseTransformDirection( swnV, viewMatrix );
  float lum = dot( diffuseColor.rgb, vec3( 0.2126, 0.7152, 0.0722 ) );
  float up = smoothstep( 0.85, 0.97, abs( swn.y ) );
  float ground = up * ( 1.0 - smoothstep( 0.3, 0.8, vStreetWorld.y ) );
  float asphalt = ( 1.0 - smoothstep( 0.07, 0.2, lum ) ) * ground;
  float puddle = smoothstep( 0.42, 0.68, streetNoise( vStreetWorld.xz * 0.35 ) * 0.7 + streetNoise( vStreetWorld.xz * 1.7 ) * 0.3 );
  // Night: the asphalt has a sheen. Rain: everything flat is soaked, with
  // standing water in the low spots, and walls run darker.
  float wet = max( uWet * asphalt * ( 0.45 + 0.55 * puddle ), uWetAll * up * ( 0.4 + 0.6 * puddle ) );
  roughnessFactor = mix( roughnessFactor, 0.05, wet );
  diffuseColor.rgb *= 1.0 - 0.38 * wet;
  float wall = uWetAll * ( 1.0 - up ) * 0.3;
  roughnessFactor = mix( roughnessFactor, 0.32, wall );
  diffuseColor.rgb *= 1.0 - 0.2 * wall;
}
#endif
`;

// After lighting: add the lamp pools as indirect light.
const FRAG_POOL = /* glsl */`
if ( uPoolI > 0.001 ) {
  vec2 puv = ( vStreetWorld.xz - uPoolRect.xy ) / uPoolRect.zw;
  if ( puv.x > 0.0 && puv.x < 1.0 && puv.y > 0.0 && puv.y < 1.0 ) {
    vec3 pool = texture2D( uPoolTex, puv ).rgb * 4.0;
    vec3 pwn = inverseTransformDirection( normal, viewMatrix );
    float up = clamp( pwn.y, 0.0, 1.0 );
    float hgt = max( vStreetWorld.y, 0.0 );
    // Lamps hang ~4.5 units up: the ground and the lower walls catch them,
    // rooftops (high AND up-facing) do not.
    float att = exp( -hgt / 3.2 ) * mix( 0.5, 1.0, up ) * ( 1.0 - smoothstep( 4.0, 6.5, hgt ) * up );
    vec3 irr = pool * uPoolI * att;
    reflectedLight.indirectDiffuse += irr * BRDF_Lambert( material.diffuseColor );
    #ifdef STREET_WET
    // Wet ground mirrors the lamp above it.
    reflectedLight.indirectSpecular += irr * max( uWet, uWetAll ) * up * ( 1.0 - material.roughness ) * 0.6;
    #endif
  }
}
`;

/**
 * Teach a lit material about the street: lamp pools always, wet asphalt when
 * `wet`. Idempotent; chains any onBeforeCompile already present.
 */
export function patchStreetMaterial(material, { wet = false } = {}) {
  if (!material || material.userData.__street) return false;
  if (!(material.isMeshStandardMaterial || material.isMeshPhysicalMaterial)) return false;
  material.userData.__street = true;
  const prev = material.onBeforeCompile;
  const prevKey = material.customProgramCacheKey?.bind(material);
  material.onBeforeCompile = function (shader, renderer) {
    prev?.call(this, shader, renderer);
    Object.assign(shader.uniforms, streetU);
    if (wet) shader.defines = { ...(shader.defines || {}), STREET_WET: '' };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${VERT_DECL}`)
      .replace('#include <project_vertex>', `#include <project_vertex>\n${VERT_BODY}`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${FRAG_DECL}`)
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>\n${FRAG_ROUGH}`)
      .replace('#include <lights_fragment_end>', `#include <lights_fragment_end>\n${FRAG_POOL}`);
  };
  material.customProgramCacheKey = () => `${prevKey ? prevKey() : ''}|street${wet ? '+wet' : ''}`;
  material.needsUpdate = true;
  return true;
}
